import React from 'react';

/**
 * FS-1079 — a label photo drawn turned by its stored rotation.
 *
 * Some label photos are stored sideways: the phone wrote a wrong EXIF tag,
 * or the label itself is mounted sideways. The file is never changed; the
 * record stores the clockwise turn that makes the label read upright, and
 * every place in the console that draws a label photo draws it through this
 * component, so they all agree.
 *
 * The box keeps the size its place gives it (`boxClassName`). The image is
 * centered in it and, for a quarter turn, laid out with the box's width and
 * height swapped before it is turned, so the turned image fills the same box
 * and never spills out of it. The box is a size container (`@container-size`),
 * so the swap is done in container units and needs no measuring.
 */

/** The turns a display applies, clockwise. */
const TURNS = [0, 90, 180, 270] as const;

/** A stored turn as one the display can apply; anything else draws the photo as it is. */
export function rotationDegrees(value: unknown): number {
  return typeof value === 'number' && (TURNS as readonly number[]).includes(value) ? value : 0;
}

/** The turn after one more quarter turn clockwise. */
export function nextRotation(value: unknown): number {
  return (rotationDegrees(value) + 90) % 360;
}

export interface LabelPhotoImageProps {
  src: string;
  alt: string;
  /** Clockwise degrees from the photo as a browser draws it; 0 or absent draws it as it is. */
  degrees?: number | null;
  /** The size and frame of the box the photo is drawn in, as its place has always drawn it. */
  boxClassName: string;
  /** How the photo fills its box: cropped to it (`cover`) or whole inside it (`contain`). */
  fit: 'cover' | 'contain';
  /** Extra look for the image itself (a faded removed photo). */
  imageClassName?: string;
  /** A box size its place computes (the zoomed large view), in place of one from classes. */
  boxStyle?: React.CSSProperties;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: () => void;
}

const LabelPhotoImage: React.FC<LabelPhotoImageProps> = ({
  src,
  alt,
  degrees,
  boxClassName,
  fit,
  imageClassName = '',
  boxStyle,
  onLoad,
  onError,
}) => {
  const turn = rotationDegrees(degrees);
  const quarter = turn === 90 || turn === 270;
  return (
    <span
      data-testid="label-photo-box"
      data-rotation={turn}
      className={`@container-size relative block overflow-hidden ${boxClassName}`}
      style={boxStyle}
    >
      <img
        src={src}
        alt={alt}
        onLoad={onLoad}
        onError={onError}
        className={`absolute left-1/2 top-1/2 max-w-none ${fit === 'cover' ? 'object-cover' : 'object-contain'} ${imageClassName}`}
        // Inline: no theme utility gives a length in container units (100cqh)
        // without an arbitrary value, and the turn is each photo's own data.
        style={{
          width: quarter ? '100cqh' : '100cqw',
          height: quarter ? '100cqw' : '100cqh',
          transform: `translate(-50%, -50%) rotate(${turn}deg)`,
        }}
      />
    </span>
  );
};

export default LabelPhotoImage;
